import React from 'react';
import { Disc, Disc3, Film, Tv } from 'lucide-react';

/**
 * Shared product-family selector used on every admin config listing page
 * (Sales Rate Cards, BOMs, Procurement Catalogs, Product Config Flows,
 * Visual Builder Assets, Quick Bundles).
 *
 * Renders 4 tabs (Vinyl / CD / DVD / Blu-ray) with optional count badges.
 * The currently-selected family controls what the listing endpoint returns
 * (the listing endpoint passes ?product_family=<family>).
 *
 * Default selected: "vinyl" — keeps existing admin UX identical to before
 * Phase 2.
 *
 * Usage:
 *   <ProductFamilyTabs
 *     value={family}
 *     onChange={setFamily}
 *     counts={{ vinyl: 12, cd: 0, dvd: 0, bluray: 0 }} // optional
 *   />
 */

export const PRODUCT_FAMILIES = [
  { id: 'vinyl', label: 'Vinyl', icon: Disc3, accent: 'amber' },
  { id: 'cd', label: 'CD', icon: Disc, accent: 'sky' },
  { id: 'dvd', label: 'DVD', icon: Film, accent: 'violet' },
  { id: 'bluray', label: 'Blu-ray', icon: Tv, accent: 'blue' },
];

const ACCENT_CLASSES = {
  amber: { active: 'bg-amber-50 text-amber-700 ring-amber-200', dot: 'bg-amber-500' },
  sky: { active: 'bg-sky-50 text-sky-700 ring-sky-200', dot: 'bg-sky-500' },
  violet: { active: 'bg-violet-50 text-violet-700 ring-violet-200', dot: 'bg-violet-500' },
  blue: { active: 'bg-blue-50 text-blue-700 ring-blue-200', dot: 'bg-blue-500' },
};

export default function ProductFamilyTabs({ value = 'vinyl', onChange, counts = null, className = '' }) {
  return (
    <div
      className={`inline-flex items-center gap-1 p-1 bg-slate-100 rounded-lg ${className}`}
      role="tablist"
      aria-label="Product family"
      data-testid="product-family-tabs"
    >
      {PRODUCT_FAMILIES.map((fam) => {
        const Icon = fam.icon;
        const isActive = value === fam.id;
        const accent = ACCENT_CLASSES[fam.accent];
        const count = counts ? counts[fam.id] ?? 0 : null;

        return (
          <button
            key={fam.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(fam.id)}
            data-testid={`product-family-tab-${fam.id}`}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              isActive
                ? `bg-white ring-1 ${accent.active} shadow-sm`
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span>{fam.label}</span>
            {count !== null && (
              <span
                className={`ml-1 inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full text-xs font-semibold ${
                  isActive ? `${accent.dot} text-white` : 'bg-slate-200 text-slate-600'
                }`}
                data-testid={`product-family-tab-${fam.id}-count`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Find the family meta (label, icon, etc.) by id.
 */
export function getProductFamilyMeta(id) {
  return PRODUCT_FAMILIES.find((f) => f.id === id) || PRODUCT_FAMILIES[0];
}
